import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Zap, ShieldCheck, Send, Star, Award } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import CategoryIcon, { CATEGORY_NAMES } from "@/components/CategoryIcon";
import SEO, { orgSchema, siteSearchSchema } from "@/components/SEO";

export default function Home() {
    const [featured, setFeatured] = useState([]);
    const [trending, setTrending] = useState([]);
    const [categories, setCategories] = useState([]);

    useEffect(() => {
        (async () => {
            const [f, t, c] = await Promise.all([
                api.get("/brands?featured=true&limit=10"),
                api.get("/brands?trending=true&limit=10"),
                api.get("/categories"),
            ]);
            setFeatured(f.data.items);
            setTrending(t.data.items);
                        setCategories(c.data.slice(0, 12));
        })();
    }, []);

    return (
        <div className="bg-rk-bg">
            <SEO
                path="/"
                keywords="gift cards india, buy gift vouchers online, tanishq gift card, makemytrip voucher, amazon gift card, nykaa gift card, corporate gifting, whatsapp gift delivery"
                structuredData={[orgSchema, siteSearchSchema]}
            />
            {/* Hero */}
            <section className="relative rk-gradient-midnight rk-noise overflow-hidden">
                <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 py-20 md:py-32 relative z-10">
                    <div className="grid md:grid-cols-2 gap-10 md:gap-20 items-center">
                        <div className="text-white rk-fade-up">
                            <div className="inline-flex items-center gap-2 border border-rk-gold/40 px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] text-rk-gold-soft mb-8">
                                <Sparkles className="w-3 h-3" /> Delivered in 10 seconds
                            </div>
                            <h1 className="font-serif text-5xl md:text-7xl lg:text-[84px] leading-[1.02] tracking-tight font-light mb-6">
                                Gift <em className="italic text-rk-gold">happiness</em>,<br />
                                rewarded.
                            </h1>
                            <p className="font-serif italic text-2xl md:text-3xl text-rk-gold-soft/90 font-light mb-8">
                                खुशियाँ भेजो.
                            </p>
                            <p className="text-white/75 text-base md:text-lg max-w-lg leading-relaxed mb-10">
                                270+ of India's most trusted brands. Instant digital gift cards, WhatsApp delivery, and exclusive discounts — wrapped in the luxury of simplicity.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link to="/catalog" data-testid="hero-shop-btn" className="rk-btn-primary inline-flex items-center gap-3">
                                    Shop The Collection <ArrowRight className="w-4 h-4" />
                                </Link>
                                <Link to="/gifts/birthday" className="rk-btn-gold-outline inline-flex items-center gap-2">
                                    Curate a Gift
                                </Link>
                            </div>

                            <div className="mt-12 flex items-center gap-8 text-white/60 text-xs uppercase tracking-[0.22em]">
                                <div>
                                    <div className="font-serif text-3xl text-rk-gold not-italic">269+</div>
                                    <div className="mt-1">Trusted Brands</div>
                                </div>
                                <div className="w-px h-10 bg-white/20" />
                                <div>
                                    <div className="font-serif text-3xl text-rk-gold">10s</div>
                                    <div className="mt-1">Delivery</div>
                                </div>
                                <div className="w-px h-10 bg-white/20" />
                                <div>
                                    <div className="font-serif text-3xl text-rk-gold">100%</div>
                                    <div className="mt-1">Genuine</div>
                                </div>
                            </div>
                        </div>

                        {/* Right visual — luxury gift card composition */}
                        <div className="relative hidden md:block">
                            <div className="relative aspect-square max-w-[520px] mx-auto">
                                <div className="absolute inset-0 rk-gradient-gold opacity-20 blur-3xl" />
                                <div className="relative z-10 grid grid-cols-2 gap-4 h-full">
                                    <div className="rk-foil p-6 flex flex-col justify-between transform -rotate-3 hover:rotate-0 transition-transform duration-500">
                                        <div className="text-[10px] uppercase tracking-[0.3em] text-rk-gold-deep">Gift Card</div>
                                        <div>
                                            <div className="font-serif text-4xl text-rk-navy">₹5,000</div>
                                            <div className="text-xs text-rk-navy/60 mt-1">MakeMyTrip · Holiday Voucher</div>
                                        </div>
                                    </div>
                                    <div className="bg-rk-navy p-6 flex flex-col justify-between border border-rk-gold/50 transform rotate-3 hover:rotate-0 transition-transform duration-500">
                                        <div className="text-[10px] uppercase tracking-[0.3em] text-rk-gold">Gift Card</div>
                                        <div>
                                            <div className="font-serif text-4xl text-rk-gold">₹10,000</div>
                                            <div className="text-xs text-white/60 mt-1">Tanishq · Jewellery</div>
                                        </div>
                                    </div>
                                    <div className="bg-rk-navy-2 p-6 flex flex-col justify-between border border-rk-gold/30 transform rotate-2 hover:rotate-0 transition-transform duration-500">
                                        <div className="text-[10px] uppercase tracking-[0.3em] text-rk-gold">Gift Card</div>
                                        <div>
                                            <div className="font-serif text-3xl text-rk-gold">₹2,500</div>
                                            <div className="text-xs text-white/60 mt-1">Taj · Hospitality</div>
                                        </div>
                                    </div>
                                    <div className="rk-foil p-6 flex flex-col justify-between transform -rotate-2 hover:rotate-0 transition-transform duration-500">
                                        <div className="text-[10px] uppercase tracking-[0.3em] text-rk-gold-deep">Gift Card</div>
                                        <div>
                                            <div className="font-serif text-3xl text-rk-navy">₹3,000</div>
                                            <div className="text-xs text-rk-navy/60 mt-1">Nykaa · Beauty</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="rk-divider-gold" />
            </section>

            {/* Trust strip */}
            <section className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 py-14 grid grid-cols-2 md:grid-cols-4 gap-0 border-b border-rk-border">
                {[
                    { icon: Zap, title: "Instant Delivery", sub: "In 10 seconds" },
                    { icon: Award, title: "269+ Brands", sub: "Curated selection" },
                    { icon: ShieldCheck, title: "100% Genuine", sub: "Verified at source" },
                    { icon: Send, title: "WhatsApp Send", sub: "One-tap gifting" },
                ].map((t, i) => (
                    <div key={i} className={`flex items-center gap-4 p-5 ${i !== 3 ? "md:border-r border-rk-border" : ""}`}>
                        <div className="w-12 h-12 border border-rk-gold/50 flex items-center justify-center shrink-0">
                            <t.icon className="w-4 h-4 text-rk-gold-deep" />
                        </div>
                        <div>
                            <div className="font-serif text-lg text-rk-navy">{t.title}</div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-rk-muted mt-0.5">{t.sub}</div>
                        </div>
                    </div>
                ))}
            </section>

            {/* Categories */}
            <section className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 py-20">
                <div className="flex items-end justify-between mb-10">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.3em] text-rk-gold-deep mb-3">The Marketplace</div>
                        <h2 className="font-serif text-4xl md:text-5xl text-rk-navy font-light">Shop by Category</h2>
                    </div>
                    <Link to="/catalog" className="hidden md:inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-rk-navy rk-link-gold">
                        View all <ArrowRight className="w-3 h-3" />
                    </Link>
                </div>
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-0 border-t border-l border-rk-border">
                    {categories.map((c) => (
                        <Link
                            to={`/catalog?category=${encodeURIComponent(c.name)}`}
                            key={c.name}
                            data-testid={`cat-tile-${c.name}`}
                            className="group bg-white border-r border-b border-rk-border py-10 px-4 text-center hover:bg-rk-navy transition-colors duration-300"
                        >
                            <div className="w-14 h-14 mx-auto mb-4 border border-rk-gold/40 flex items-center justify-center text-rk-gold-deep group-hover:border-rk-gold group-hover:text-rk-gold group-hover:scale-110 transition-all duration-300">
                                <CategoryIcon name={c.name} size={22} />
                            </div>
                            <div className="font-serif text-base text-rk-navy group-hover:text-rk-gold leading-tight">{c.name}</div>
                            <div className="text-[10px] uppercase tracking-wider text-rk-muted group-hover:text-rk-gold/70 mt-1.5">{c.count} brands</div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* Trending */}
            <Shelf title="Trending Now" subtitle="The most coveted this week" items={trending} cta="/catalog?trending=true" />

            {/* Price bands */}
            <section className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 py-20">
                <div className="mb-10 text-center">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-rk-gold-deep mb-3">Curated by Budget</div>
                    <h2 className="font-serif text-4xl md:text-5xl text-rk-navy font-light">For Every Occasion</h2>
                    <div className="w-16 rk-divider-gold mx-auto mt-5" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-l border-rk-border">
                    {[
                        { label: "Under ₹500", amount: 500, sub: "Small thank-yous" },
                        { label: "Under ₹2,000", amount: 2000, sub: "Everyday delights" },
                        { label: "Premium ₹5,000+", amount: 50000, sub: "Statement gifts" },
                    ].map((p, i) => (
                        <Link
                            to={`/catalog?max_price=${p.amount}`}
                            key={i}
                            data-testid={`price-band-${i}`}
                            className="group relative bg-white border-r border-b border-rk-border p-10 hover:bg-rk-navy transition-colors duration-500"
                        >
                            <div className="text-[10px] uppercase tracking-[0.3em] text-rk-gold-deep group-hover:text-rk-gold mb-3">{p.sub}</div>
                            <h3 className="font-serif text-4xl text-rk-navy group-hover:text-rk-gold font-light mb-4">{p.label}</h3>
                            <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-rk-navy/70 group-hover:text-rk-gold font-semibold">
                                Explore <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* Featured */}
            <div className="bg-rk-cream">
                <Shelf title="Curator's Picks" subtitle="Timeless favourites across India" items={featured} cta="/catalog?featured=true" dark />
            </div>

            {/* Occasions */}
            <section className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 py-20">
                <div className="text-center mb-10">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-rk-gold-deep mb-3">Gift for every</div>
                    <h2 className="font-serif text-4xl md:text-5xl text-rk-navy font-light">Occasion</h2>
                    <div className="w-16 rk-divider-gold mx-auto mt-5" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-0 border-t border-l border-rk-border">
                    {[
                        { slug: "birthday", label: "Birthday", emoji: "🎂" },
                        { slug: "anniversary", label: "Anniversary", emoji: "💖" },
                        { slug: "wedding", label: "Wedding", emoji: "💒" },
                        { slug: "diwali", label: "Diwali", emoji: "🪔" },
                        { slug: "rakhi", label: "Rakhi", emoji: "🎀" },
                        { slug: "corporate", label: "Corporate", emoji: "💼" },
                    ].map((o) => (
                        <Link
                            to={`/gifts/${o.slug}`}
                            key={o.slug}
                            data-testid={`occasion-${o.slug}`}
                            className="group bg-white border-r border-b border-rk-border p-8 text-center hover:bg-rk-navy transition-colors duration-500"
                        >
                            <div className="text-5xl mb-4 group-hover:scale-110 transition-transform duration-500">{o.emoji}</div>
                            <div className="font-serif text-xl text-rk-navy group-hover:text-rk-gold">{o.label}</div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* Testimonial */}
            <section className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 pb-24">
                <div className="rk-gradient-navy rk-noise p-10 md:p-20 text-white grid md:grid-cols-2 gap-12 items-center relative overflow-hidden">
                    <div className="relative z-10">
                        <div className="flex items-center gap-1 mb-5">
                            {Array.from({ length: 5 }).map((_, i) => (
                                <Star key={i} className="w-4 h-4 fill-rk-gold text-rk-gold" />
                            ))}
                        </div>
                        <blockquote className="font-serif text-3xl md:text-5xl leading-[1.1] font-light italic">
                            "I sent a Tanishq card to my mother. She got it on WhatsApp in seconds. Her eyes lit up — and so did mine."
                        </blockquote>
                        <p className="mt-6 text-xs uppercase tracking-[0.26em] text-rk-gold">— Ananya K. · Mumbai</p>
                    </div>
                    <div className="relative z-10 border border-rk-gold/40 p-8 md:p-10">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-rk-gold mb-3">The LoyltyKart Promise</div>
                        <div className="font-serif text-5xl md:text-6xl text-rk-gold font-light mb-4">10s.</div>
                        <p className="text-white/75 text-base leading-relaxed">
                            From brand selection to delivery into your loved one's WhatsApp.
                            No paper. No waiting. Just the quiet, confident pleasure of a perfectly timed gift.
                        </p>
                        <div className="rk-divider-gold mt-6" />
                    </div>
                </div>
            </section>
        </div>
    );
}

function Shelf({ title, subtitle, items, cta, dark = false }) {
    if (!items || !items.length) return null;
    return (
        <section className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 py-20">
            <div className="flex items-end justify-between mb-10">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-rk-gold-deep mb-3">{subtitle}</div>
                    <h2 className="font-serif text-4xl md:text-5xl text-rk-navy font-light">{title}</h2>
                </div>
                <Link to={cta} className="hidden md:inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold text-rk-navy rk-link-gold">
                    Browse all <ArrowRight className="w-3 h-3" />
                </Link>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                {items.slice(0, 10).map((b) => (
                    <ProductCard key={b.id} brand={b} />
                ))}
            </div>
        </section>
    );
}
